import { css } from '@emotion/css';
import { ActionIcon, Box, Group, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconMail, IconMailOpened, IconTrash, IconUser } from '@tabler/icons-react';
import { ExternalMailAction } from 'app/Private/ExternalMailbox/ExternalMailActions/ExternalMailAction';
import { ExternalMailActionAccountManager } from 'app/Private/ExternalMailbox/ExternalMailActions/ExternalMailActionAccountManager';
import { ExternalMailActionDebtor } from 'app/Private/ExternalMailbox/ExternalMailActions/ExternalMailActionDebtor';
import dayjs from 'dayjs';
import { i18nKeys, tKeys, useTranslation } from 'locales';
import type { LightThread } from 'modules/Mails/hooks/use-load-mails';
import { useDeleteMail } from 'shared/hooks/use-delete-mail';
import { useUpdateThread } from 'shared/hooks/use-update-thread';

type MailDateCellProps = {
  mail: LightThread;
  rowBg?: string;
};

export const MailDateCell = ({ mail, rowBg }: MailDateCellProps) => {
  const { t } = useTranslation();

  const { updateThread } = useUpdateThread();
  const { deleteMail } = useDeleteMail();

  const { id } = mail;
  const { read, date } = mail.mailDetail;

  const getFormattedDate = () => {
    const d = dayjs(date);
    const now = dayjs();

    if (d.isSame(now, 'day')) return d.format('HH:mm');
    if (d.isSame(now, 'week')) return d.format('dddd');
    if (d.isSame(now, 'year')) return d.format('D MMM');
    return d.format('D MMM YYYY');
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    modals.openConfirmModal({
      title: t(tKeys.CONFIRM),
      centered: true,
      children: <Text>{t(tKeys.AYS.DELETE_EXTERNAL_MAIL)}</Text>,
      labels: { confirm: t(tKeys.DELETE), cancel: t(tKeys.CANCEL) },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteMail({ id }),
    });
  };

  const handleAssign = (e: React.MouseEvent) => {
    e.stopPropagation();
    modals.open({
      title: t(i18nKeys.TASK.ACTIONS.ASSIGN),
      centered: true,
      children: (
        <ExternalMailAction
          mail={mail}
          onSubmit={data => {
            updateThread(
              { id, debtorId: data.debtorId, accountManagerId: data.userId },
              { onSuccess: () => modals.closeAll() },
            );
          }}
        >
          <ExternalMailActionDebtor />
          <ExternalMailActionAccountManager />
        </ExternalMailAction>
      ),
    });
  };

  return (
    <Box className={cellClass}>
      <Text ta="end" size="sm" className="date" fw={read ? undefined : 800}>
        {getFormattedDate()}
      </Text>
      <Group
        gap={4}
        wrap="nowrap"
        className="actions"
        style={{
          position: 'absolute',
          top: '50%',
          right: 0,
          transform: 'translateY(-50%)',
          backgroundColor: rowBg,
        }}
      >
        <Tooltip
          label={t(
            read ? i18nKeys.EXTERNAL_MAIL.ACTIONS.UNREAD : i18nKeys.EXTERNAL_MAIL.ACTIONS.READ,
          )}
          withArrow
        >
          <ActionIcon
            variant="light"
            color="gray"
            onClick={e => {
              e.stopPropagation();
              updateThread({ id, read: !read });
            }}
          >
            {read ? <IconMailOpened stroke={1.5} size={16} /> : <IconMail stroke={1.5} size={16} />}
          </ActionIcon>
        </Tooltip>
        <Tooltip label={t(i18nKeys.TASK.ACTIONS.ASSIGN)} withArrow>
          <ActionIcon variant="light" color="gray" onClick={handleAssign}>
            <IconUser stroke={1.5} size={16} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={t(i18nKeys.DELETE)} withArrow>
          <ActionIcon variant="light" color="red" onClick={handleDelete}>
            <IconTrash stroke={1.5} size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Box>
  );
};

const cellClass = css({
  position: 'relative',
  whiteSpace: 'nowrap',
  '& .date': { visibility: 'visible' },
  '& .actions': { visibility: 'hidden' },
  'tr:hover &': {
    '& .date': { visibility: 'hidden' },
    '& .actions': {
      visibility: 'visible',
      backgroundColor: 'var(--table-highlight-on-hover-color)',
    },
  },
});
